import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { InAppNotificationListItem } from '@quality-vet/contracts';

import { Modal } from '../components/modal';
import {
  formatDate,
  isAbortError,
  readApiError,
  supersededRequestError,
  toIso,
} from '../utils/communication-helpers';
import '../components/app-utility-bar.css';
import './notifications-panel.css';

export type NotificationsPanelProps = {
  apiUrl: string;
  accessToken: string;
  tenantId: string;
  tenantName?: string;
  currentUserId: string;
  isPlatformAdmin: boolean;
  tenantRoles?: string[];
  timeZone?: string;
  initialNotificationId?: string;
  onInitialNotificationConsumed?: () => void;
  onNotificationRead?: () => void;
};

type ApiRequest = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
};

function searchIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <circle cx="10.5" cy="10.5" r="5.5" />
      <path d="m15 15 4 4" />
    </svg>
  );
}

export function NotificationsPage(props: NotificationsPanelProps) {
  const [notifications, setNotifications] = useState<InAppNotificationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNotification, setSelectedNotification] =
    useState<InAppNotificationListItem | null>(null);
  const [detailStatus, setDetailStatus] = useState('');
  const [publishFormOpen, setPublishFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState<'tenant' | 'platform'>(() => {
    const roles = new Set(props.tenantRoles ?? []);
    return props.tenantId && (roles.has('tenant_admin') || roles.has('manager'))
      ? 'tenant'
      : 'platform';
  });
  const [sending, setSending] = useState(false);
  const [publishStatus, setPublishStatus] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState<'published' | 'draft' | 'scheduled'>(
    'published',
  );
  const [scheduledFor, setScheduledFor] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [acknowledgementRequired, setAcknowledgementRequired] = useState(false);
  const [securityMandatory, setSecurityMandatory] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [readReceiptsEnabled, setReadReceiptsEnabled] = useState(false);
  const [messageRetentionDays, setMessageRetentionDays] = useState(400);
  const [notificationRetentionDays, setNotificationRetentionDays] = useState(400);
  const [calendarRetentionDays, setCalendarRetentionDays] = useState(400);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState('');
  const requestGenerationRef = useRef(0);
  const requestControllersRef = useRef(new Set<AbortController>());

  const selectedRoles = new Set(props.tenantRoles ?? []);
  const canPublishTenantNotification =
    selectedRoles.has('tenant_admin') || selectedRoles.has('manager');
  const canManagePolicy = Boolean(props.tenantRoles?.some((role) => role === 'tenant_admin'));
  const canPublish = props.isPlatformAdmin || canPublishTenantNotification;

  useEffect(() => {
    if (!props.tenantId || !canPublishTenantNotification) setScope('platform');
  }, [canPublishTenantNotification, props.tenantId]);

  useEffect(() => {
    requestGenerationRef.current += 1;
    for (const controller of requestControllersRef.current) controller.abort();
    requestControllersRef.current.clear();

    setNotifications([]);
    setLoading(false);
    setError('');
    setSelectedNotification(null);

    return () => {
      requestGenerationRef.current += 1;
      for (const controller of requestControllersRef.current) controller.abort();
      requestControllersRef.current.clear();
    };
  }, [props.accessToken, props.apiUrl, props.tenantId]);

  const request = async <T,>(path: string, requestOptions: ApiRequest = {}): Promise<T> => {
    const generation = requestGenerationRef.current;
    const controller = new AbortController();
    requestControllersRef.current.add(controller);
    try {
      const response = await fetch(`${props.apiUrl}${path}`, {
        method: requestOptions.method ?? 'GET',
        headers: {
          authorization: `Bearer ${props.accessToken}`,
          ...(requestOptions.body === undefined ? {} : { 'content-type': 'application/json' }),
        },
        ...(requestOptions.body === undefined ? {} : { body: JSON.stringify(requestOptions.body) }),
        signal: controller.signal,
      });
      if (generation !== requestGenerationRef.current) throw supersededRequestError();
      if (!response.ok) {
        const message = await readApiError(response);
        if (generation !== requestGenerationRef.current) throw supersededRequestError();
        throw new Error(message);
      }
      const payload = (await response.json()) as T;
      if (generation !== requestGenerationRef.current) throw supersededRequestError();
      return payload;
    } finally {
      requestControllersRef.current.delete(controller);
    }
  };

  const tenantPath = (resource: string) =>
    `/v1/tenants/${encodeURIComponent(props.tenantId)}/${resource}`;

  const loadNotifications = async () => {
    const generation = requestGenerationRef.current;
    setLoading(true);
    setError('');
    try {
      if (!props.tenantId) {
        const payload = await request<{ items: InAppNotificationListItem[] }>(
          '/v1/platform/notifications',
        );
        setNotifications(payload.items);
        return;
      }
      const payload = await request<{
        items: InAppNotificationListItem[];
        unreadCount: number;
      }>(tenantPath('in-app-notifications'));
      const managementPayloads = await Promise.all([
        canPublishTenantNotification
          ? request<{ items: InAppNotificationListItem[] }>(
              tenantPath('in-app-notifications/manage'),
            )
          : Promise.resolve({ items: [] }),
        props.isPlatformAdmin
          ? request<{ items: InAppNotificationListItem[] }>('/v1/platform/notifications')
          : Promise.resolve({ items: [] }),
      ]);
      const visibleById = new Map(payload.items.map((item) => [item.id, item]));
      const combinedById = new Map<string, InAppNotificationListItem>();
      for (const item of [...managementPayloads[0].items, ...managementPayloads[1].items]) {
        combinedById.set(item.id, { ...item, ...visibleById.get(item.id) });
      }
      for (const item of payload.items) combinedById.set(item.id, item);
      setNotifications(
        [...combinedById.values()].sort((left, right) =>
          right.createdAt.localeCompare(left.createdAt),
        ),
      );
    } catch (loadError) {
      if (isAbortError(loadError) || generation !== requestGenerationRef.current) return;
      setError(loadError instanceof Error ? loadError.message : 'Notifications could not load.');
    } finally {
      if (generation === requestGenerationRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadNotifications();
  }, [props.accessToken, props.apiUrl, props.tenantId]);

  useEffect(() => {
    if (!props.initialNotificationId || notifications.length === 0) return;
    if (selectedNotification) return;
    const target = notifications.find((item) => item.id === props.initialNotificationId);
    if (target) {
      setSelectedNotification(target);
      setDetailStatus('');
      if (!target.readAt) void markNotificationRead(target);
    }
    if (props.onInitialNotificationConsumed) props.onInitialNotificationConsumed();
  }, [notifications, props.initialNotificationId]);

  const markNotificationRead = async (notification: InAppNotificationListItem) => {
    if (!props.tenantId || notification.readAt) return;
    setDetailStatus('');
    try {
      const marker = await request<{ readAt: string }>(
        tenantPath(`in-app-notifications/${encodeURIComponent(notification.id)}/read`),
        { method: 'PUT' },
      );
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id ? { ...item, readAt: marker.readAt } : item,
        ),
      );
      setSelectedNotification((current) =>
        current?.id === notification.id ? { ...current, readAt: marker.readAt } : current,
      );
      setDetailStatus('Marked as read.');
      props.onNotificationRead?.();
    } catch (markError) {
      if (isAbortError(markError)) return;
      setDetailStatus(
        markError instanceof Error ? markError.message : 'Notification could not update.',
      );
    }
  };

  const acknowledge = async (notification: InAppNotificationListItem) => {
    if (!props.tenantId) return;
    setDetailStatus('');
    try {
      await request(
        tenantPath(`in-app-notifications/${encodeURIComponent(notification.id)}/acknowledge`),
        { method: 'PUT' },
      );
      await loadNotifications();
      setSelectedNotification((current) =>
        current?.id === notification.id
          ? { ...current, acknowledgedAt: new Date().toISOString() }
          : current,
      );
      setDetailStatus('Notification acknowledged.');
    } catch (acknowledgementError) {
      if (isAbortError(acknowledgementError)) return;
      setDetailStatus(
        acknowledgementError instanceof Error
          ? acknowledgementError.message
          : 'Notification could not be acknowledged.',
      );
    }
  };

  const withdrawNotification = async (notification: InAppNotificationListItem) => {
    if (!props.isPlatformAdmin || notification.scope !== 'platform') return;
    setDetailStatus('');
    try {
      await request(`/v1/platform/notifications/${encodeURIComponent(notification.id)}`, {
        method: 'PATCH',
        body: { status: 'withdrawn' },
      });
      await loadNotifications();
      setSelectedNotification(null);
      setDetailStatus('Whole-of-app notification withdrawn.');
    } catch (withdrawError) {
      if (isAbortError(withdrawError)) return;
      setDetailStatus(
        withdrawError instanceof Error
          ? withdrawError.message
          : 'Notification could not be withdrawn.',
      );
    }
  };

  const publish = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSending(true);
    setPublishStatus('');
    try {
      const path =
        scope === 'platform' ? '/v1/platform/notifications' : tenantPath('in-app-notifications');
      await request(path, {
        method: 'POST',
        body: {
          title,
          body,
          status: deliveryStatus,
          ...(deliveryStatus === 'scheduled' && scheduledFor
            ? { scheduledFor: toIso(scheduledFor, props.timeZone) }
            : {}),
          ...(expiresAt ? { expiresAt: toIso(expiresAt, props.timeZone) } : {}),
          acknowledgementRequired,
          securityMandatory,
        },
      });
      setTitle('');
      setBody('');
      setPublishStatus(
        scope === 'platform'
          ? 'Whole-of-app notification published.'
          : `Notification published to ${props.tenantName ?? 'this organisation'}.`,
      );
      setPublishFormOpen(false);
      await loadNotifications();
    } catch (publishError) {
      if (isAbortError(publishError)) return;
      setPublishStatus(
        publishError instanceof Error ? publishError.message : 'Notification could not publish.',
      );
    } finally {
      setSending(false);
    }
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      notification.title.toLowerCase().includes(query) ||
      notification.body.toLowerCase().includes(query)
    );
  });

  const openSettings = async () => {
    setSettingsOpen(true);
    setSettingsLoading(true);
    setSettingsStatus('');
    try {
      const prefs = await request<{ preferences: { inAppEnabled: boolean } }>(
        tenantPath('communication-preferences'),
      );
      setInAppEnabled(prefs.preferences.inAppEnabled);
      if (canManagePolicy) {
        const policy = await request<{
          policy: {
            readReceiptsEnabled: boolean;
            messageRetentionDays: number;
            notificationRetentionDays: number;
            calendarRetentionDays: number;
          };
        }>(tenantPath('communication-policy'));
        setReadReceiptsEnabled(policy.policy.readReceiptsEnabled);
        setMessageRetentionDays(policy.policy.messageRetentionDays);
        setNotificationRetentionDays(policy.policy.notificationRetentionDays);
        setCalendarRetentionDays(policy.policy.calendarRetentionDays);
      }
    } catch (loadError) {
      if (isAbortError(loadError)) return;
      setSettingsStatus(
        loadError instanceof Error ? loadError.message : 'Settings could not load.',
      );
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSettingsSaving(true);
    setSettingsStatus('');
    try {
      await request(tenantPath('communication-preferences'), {
        method: 'PUT',
        body: { inAppEnabled },
      });
      if (canManagePolicy) {
        await request(tenantPath('communication-policy'), {
          method: 'PATCH',
          body: {
            readReceiptsEnabled,
            messageRetentionDays,
            notificationRetentionDays,
            calendarRetentionDays,
          },
        });
      }
      setSettingsStatus('Settings saved.');
    } catch (saveError) {
      if (isAbortError(saveError)) return;
      setSettingsStatus(
        saveError instanceof Error ? saveError.message : 'Settings could not be saved.',
      );
    } finally {
      setSettingsSaving(false);
    }
  };

  return (
    <div className="notifications-page-container">
      <header className="notifications-page-header">
        <h1>Notifications</h1>
        <div className="notifications-page-header-actions">
          <label className="notifications-search-wrapper">
            <span className="notifications-search-label">Search notifications</span>
            <span className="notifications-search-control">
              {searchIcon()}
              <input
                className="notifications-search-input"
                placeholder="Search notifications"
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </span>
          </label>
          {canPublish ? (
            <button
              className="notifications-publish-button"
              type="button"
              onClick={() => setPublishFormOpen(true)}
            >
              Publish notification
            </button>
          ) : null}
          {props.tenantId ? (
            <button
              className="notifications-publish-button"
              type="button"
              onClick={() => void openSettings()}
            >
              Settings
            </button>
          ) : null}
        </div>
      </header>

      <section aria-label="Notification list" className="notifications-page-list">
        {loading ? <p className="notifications-page-empty">Loading notifications...</p> : null}
        {error ? (
          <p className="notifications-page-empty notifications-page-error" role="alert">
            {error}
          </p>
        ) : null}
        {!loading && !error && filteredNotifications.length === 0 ? (
          <p className="notifications-page-empty">
            {searchQuery ? 'No notifications match your search.' : 'No notifications yet.'}
          </p>
        ) : null}
        {filteredNotifications.map((notification) => (
          <button
            className={`notifications-page-item${notification.readAt ? '' : ' utility-item-unread'}`}
            key={notification.id}
            type="button"
            onClick={() => {
              setSelectedNotification(notification);
              setDetailStatus('');
              if (!notification.readAt) void markNotificationRead(notification);
            }}
          >
            <div className="notifications-page-item-content">
              <span className="utility-scope-label">
                {notification.scope === 'platform'
                  ? 'Whole of app'
                  : (props.tenantName ?? 'Organisation')}
              </span>
              <h3>{notification.title}</h3>
              <p>{notification.body}</p>
            </div>
            <time dateTime={notification.createdAt}>
              {formatDate(notification.createdAt, true, props.timeZone)}
            </time>
          </button>
        ))}
      </section>

      {selectedNotification ? (
        <Modal label="Notification details" onClose={() => setSelectedNotification(null)}>
          <div className="notification-detail-modal">
            <header className="notification-detail-header">
              <span className="utility-scope-label">
                {selectedNotification.scope === 'platform'
                  ? 'Whole of app'
                  : (props.tenantName ?? 'Organisation')}
              </span>
              <h2>{selectedNotification.title}</h2>
              <time dateTime={selectedNotification.createdAt}>
                {formatDate(selectedNotification.createdAt, true, props.timeZone)}
              </time>
            </header>
            <div className="notification-detail-body">
              <p>{selectedNotification.body}</p>
            </div>
            <dl className="notification-detail-meta">
              <dt>Status</dt>
              <dd>{selectedNotification.status}</dd>
              <dt>Version</dt>
              <dd>{selectedNotification.version}</dd>
              {selectedNotification.readAt ? (
                <>
                  <dt>Read at</dt>
                  <dd>{formatDate(selectedNotification.readAt, true, props.timeZone)}</dd>
                </>
              ) : null}
              {selectedNotification.acknowledgedAt ? (
                <>
                  <dt>Acknowledged at</dt>
                  <dd>{formatDate(selectedNotification.acknowledgedAt, true, props.timeZone)}</dd>
                </>
              ) : null}
            </dl>
            {detailStatus ? (
              <p
                aria-atomic="true"
                aria-live="polite"
                className="notification-detail-status"
                role="status"
              >
                {detailStatus}
              </p>
            ) : null}
            <div className="notification-detail-actions">
              {!selectedNotification.readAt && props.tenantId ? (
                <button
                  className="utility-secondary-button"
                  type="button"
                  onClick={() => void markNotificationRead(selectedNotification)}
                >
                  Mark as read
                </button>
              ) : null}
              {selectedNotification.acknowledgementRequired &&
              !selectedNotification.acknowledgedAt &&
              props.tenantId ? (
                <button
                  className="utility-secondary-button"
                  type="button"
                  onClick={() => void acknowledge(selectedNotification)}
                >
                  Acknowledge
                </button>
              ) : null}
              {props.isPlatformAdmin &&
              selectedNotification.scope === 'platform' &&
              selectedNotification.status !== 'withdrawn' ? (
                <button
                  className="utility-danger-button"
                  type="button"
                  onClick={() => void withdrawNotification(selectedNotification)}
                >
                  Withdraw
                </button>
              ) : null}
            </div>
          </div>
        </Modal>
      ) : null}

      {publishFormOpen ? (
        <Modal label="Publish notification" onClose={() => setPublishFormOpen(false)}>
          <div className="notification-publish-modal">
            <h2>Publish a notification</h2>
            <form className="utility-editor" onSubmit={(event) => void publish(event)}>
              {props.isPlatformAdmin ? (
                <label>
                  Audience
                  <select
                    value={scope}
                    onChange={(event) => setScope(event.target.value as typeof scope)}
                  >
                    {props.tenantId && canPublishTenantNotification ? (
                      <option value="tenant">{props.tenantName ?? 'Selected organisation'}</option>
                    ) : null}
                    <option value="platform">Whole of app</option>
                  </select>
                </label>
              ) : null}
              <label>
                Title
                <input
                  maxLength={120}
                  required
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </label>
              <label>
                Delivery
                <select
                  value={deliveryStatus}
                  onChange={(event) =>
                    setDeliveryStatus(event.target.value as typeof deliveryStatus)
                  }
                >
                  <option value="published">Publish now</option>
                  <option value="scheduled">Schedule</option>
                  <option value="draft">Save draft</option>
                </select>
              </label>
              {deliveryStatus === 'scheduled' ? (
                <label>
                  Publish at
                  <input
                    required
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(event) => setScheduledFor(event.target.value)}
                  />
                </label>
              ) : null}
              <label>
                Expires <span>(optional)</span>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                />
              </label>
              <label className="utility-checkbox-label">
                <input
                  checked={acknowledgementRequired}
                  type="checkbox"
                  onChange={(event) => setAcknowledgementRequired(event.target.checked)}
                />
                Require acknowledgement
              </label>
              {canManagePolicy ? (
                <label className="utility-checkbox-label">
                  <input
                    checked={securityMandatory}
                    type="checkbox"
                    onChange={(event) => setSecurityMandatory(event.target.checked)}
                  />
                  Mandatory security notice
                </label>
              ) : null}
              <label>
                Message
                <textarea
                  maxLength={2000}
                  required
                  rows={4}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                />
              </label>
              {publishStatus ? (
                <p
                  aria-atomic="true"
                  aria-live="polite"
                  className="notification-detail-status"
                  role="status"
                >
                  {publishStatus}
                </p>
              ) : null}
              <div className="utility-item-actions">
                <button disabled={sending} type="submit">
                  {sending ? 'Working...' : 'Save notification'}
                </button>
                <button disabled={sending} type="button" onClick={() => setPublishFormOpen(false)}>
                  Dismiss
                </button>
              </div>
            </form>
          </div>
        </Modal>
      ) : null}

      {settingsOpen ? (
        <Modal label="Communication settings" onClose={() => setSettingsOpen(false)}>
          <div className="notification-publish-modal">
            <h2>Communication settings</h2>
            {settingsLoading ? (
              <p className="notification-detail-status">Loading settings...</p>
            ) : (
              <form className="utility-editor" onSubmit={(event) => void saveSettings(event)}>
                <div className="utility-preferences">
                  <p>Personal preferences</p>
                  <label>
                    <input
                      checked={inAppEnabled}
                      type="checkbox"
                      onChange={(event) => setInAppEnabled(event.target.checked)}
                    />
                    In-app notifications enabled
                  </label>
                </div>
                {canManagePolicy ? (
                  <div className="utility-preferences">
                    <p>Organisation policy</p>
                    <label>
                      <input
                        checked={readReceiptsEnabled}
                        type="checkbox"
                        onChange={(event) => setReadReceiptsEnabled(event.target.checked)}
                      />
                      Read receipts enabled
                    </label>
                    <label>
                      Message retention (days)
                      <input
                        max={2555}
                        min={30}
                        type="number"
                        value={messageRetentionDays}
                        onChange={(event) => setMessageRetentionDays(Number(event.target.value))}
                      />
                    </label>
                    <label>
                      Notification retention (days)
                      <input
                        max={2555}
                        min={30}
                        type="number"
                        value={notificationRetentionDays}
                        onChange={(event) =>
                          setNotificationRetentionDays(Number(event.target.value))
                        }
                      />
                    </label>
                    <label>
                      Calendar retention (days)
                      <input
                        max={2555}
                        min={30}
                        type="number"
                        value={calendarRetentionDays}
                        onChange={(event) => setCalendarRetentionDays(Number(event.target.value))}
                      />
                    </label>
                  </div>
                ) : null}
                {settingsStatus ? (
                  <p
                    aria-atomic="true"
                    aria-live="polite"
                    className="notification-detail-status"
                    role="status"
                  >
                    {settingsStatus}
                  </p>
                ) : null}
                <div className="utility-item-actions">
                  <button disabled={settingsSaving} type="submit">
                    {settingsSaving ? 'Saving...' : 'Save settings'}
                  </button>
                  <button
                    disabled={settingsSaving}
                    type="button"
                    onClick={() => setSettingsOpen(false)}
                  >
                    Dismiss
                  </button>
                </div>
              </form>
            )}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
